import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiTruck, FiCheck, FiAlertCircle } from 'react-icons/fi';
import { adminApi } from '../adminApi';

function AdminBookOrders() {
  const { state } = useLocation();
  const navigate  = useNavigate();
  const orderIds  = state?.orderIds || [];

  const [rows,     setRows]     = useState([]);
  const [cities,   setCities]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [booking,  setBooking]  = useState(false);
  const [results,  setResults]  = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // ── Friendly error messages ────────────────────────────────────────────────
  function friendlyError(raw) {
    if (!raw) return 'Booking failed. Please check the order details and try again.';
    const msg = String(raw).trim();
    if (!msg || msg.startsWith('{') || msg.startsWith('[')) {
      return 'Booking failed. Please check the order details and try again.';
    }
    const low = msg.toLowerCase();
    if (low.includes('cod') && low.includes('100')) return 'COD amount must be at least Rs 100.';
    if (low.includes('city')) return 'Invalid destination city selected.';
    if (low.includes('cell') || low.includes('phone')) return 'Invalid phone number format.';
    if (low.includes('login') || low.includes('credential')) return 'CallCourier login failed. Check Courier settings.';
    return msg;
  }

  useEffect(() => {
    if (!orderIds.length) { navigate('/orders'); return; }

    (async () => {
      try {
        const [citiesRes, ...orderResults] = await Promise.all([
          adminApi.getCCCities(),
          ...orderIds.map(id => adminApi.getOrder(id).then(r => r.data).catch(() => null)),
        ]);

        const list = (Array.isArray(citiesRes.data) ? citiesRes.data : [])
          .slice()
          .sort((a, b) => (a.CityName || '').localeCompare(b.CityName || ''));
        setCities(list);

        const newRows = orderResults.filter(Boolean).map(o => {
          const name    = o.user?.full_name || o.user?.username || o.guest_name || '';
          const desc    = o.items?.map(i => `${i.name} Qty=${i.quantity}`).join(', ') || '';
          const pcs     = o.items?.reduce((s, i) => s + i.quantity, 0) || 1;
          const amount  = (o.total_amount || 0) + (o.shipping_amount || 0);
          const isPaid  = o.payment_method === 'paid' || o.payment_method === 'prepaid';

          // auto-detect city from last address segment; leave empty if no match
          let detectedCity   = '';
          let detectedCityName = '';
          if (o.delivery_address && list.length > 0) {
            const segs = o.delivery_address.split(',').map(p => p.trim().toUpperCase());
            const rev  = [...segs].reverse();
            const hit  = list.find(c => rev.some(s => s === (c.CityName || '').toUpperCase()));
            if (hit) { detectedCity = hit.CityId; detectedCityName = hit.CityName; }
          }

          return {
            order_id:       o.id,
            order_number:   `#${o.id}`,
            consignee_name: name,
            phone:          o.phone || '',
            address:        o.delivery_address || '',
            cod_amount:     isPaid ? 0 : amount,
            weight:         '0.5',
            pcs:            String(pcs),
            description:    desc,
            note:           '',
            dest_city_id:   detectedCity,
            dest_city_name: detectedCityName,
          };
        });

        setRows(newRows);
        setSelected(new Set(newRows.map(r => r.order_id)));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line

  const upd = (orderId, field, val) =>
    setRows(prev => prev.map(r => r.order_id === orderId ? { ...r, [field]: val } : r));

  const updCity = (orderId, name) => {
    const match = cities.find(c => (c.CityName || '').toLowerCase() === name.trim().toLowerCase());
    setRows(prev => prev.map(r => r.order_id === orderId
      ? { ...r, dest_city_name: name, dest_city_id: match ? match.CityId : '' }
      : r));
  };

  const toggleRow = id => setSelected(prev => {
    const s = new Set(prev);
    s.has(id) ? s.delete(id) : s.add(id);
    return s;
  });

  const allChecked = rows.length > 0 && selected.size === rows.length;
  const toggleAll  = () => setSelected(allChecked ? new Set() : new Set(rows.map(r => r.order_id)));

  const handleBook = async () => {
    const toBook = rows.filter(r => selected.has(r.order_id));
    if (!toBook.length) return;
    if (toBook.some(r => !r.dest_city_id)) {
      setSubmitAttempted(true);
      return;
    }
    setSubmitAttempted(false);
    setBooking(true);
    try {
      const bookings = toBook.map(r => ({
        order_id:       r.order_id,
        dest_city_id:   r.dest_city_id,
        dest_city_name: r.dest_city_name,
        consignee_name: r.consignee_name,
        phone:          r.phone,
        address:        r.address,
        cod_amount:     parseFloat(r.cod_amount)  || 0,
        weight:         parseFloat(r.weight)       || 0.5,
        pcs:            parseInt(r.pcs)            || 1,
        description:    r.description,
        note:           r.note,
      }));
      const res = await adminApi.bookPostex({ bookings });
      setResults(res.data.results);
    } catch (err) {
      setResults([{ order_id: '—', success: false, error: err.response?.data?.detail || 'Request failed' }]);
    } finally {
      setBooking(false);
    }
  };

  if (loading) return (
    <div className="admin-loading"><div className="admin-spinner" />Loading orders…</div>
  );

  // ── Results view ──────────────────────────────────────────────────────────
  if (results) return (
    <div className="book-page">
      <div className="book-page-header">
        <button className="btn-admin btn-admin-ghost back-btn" onClick={() => navigate('/orders')}>
          <FiArrowLeft size={14} /> Back to Orders
        </button>
        <h2 className="book-page-title">Booking Results</h2>
        <span />
      </div>
      <div className="book-results-list">
        {results.map((r, i) => (
          <div key={i} className={`booking-result-row ${r.success ? 'result-ok' : 'result-fail'}`}>
            <span className="result-order">Order #{r.order_id}</span>
            {r.success
              ? <span className="result-tracking">
                  <FiCheck size={14} /> CN: <strong>{r.tracking}</strong>
                  {!String(r.tracking).toUpperCase().startsWith('CX-') && (
                    <>
                      {' · '}
                      <a
                        href={`http://cod.callcourier.com.pk/Booking/AfterSavePublic/${r.tracking}`}
                        target="_blank" rel="noreferrer" className="cn-slip-link"
                      >Print Slip ↗</a>
                    </>
                  )}
                </span>
              : <span className="result-error"><FiAlertCircle size={13} /> {friendlyError(r.error)}</span>
            }
          </div>
        ))}
      </div>
      <div style={{ padding: '20px 24px' }}>
        <button className="btn-admin btn-admin-ghost" onClick={() => navigate('/orders')}>
          <FiArrowLeft size={14} /> Back to Orders
        </button>
      </div>
    </div>
  );

  // ── Booking table view ────────────────────────────────────────────────────
  return (
    <div className="book-page">
      <div className="book-page-header">
        <button className="btn-admin btn-admin-ghost back-btn" onClick={() => navigate('/orders')}>
          <FiArrowLeft size={14} /> Back
        </button>
        <h2 className="book-page-title">Custom Orders Booking</h2>
        <button
          className="btn-admin btn-fulfill"
          onClick={handleBook}
          disabled={booking || selected.size === 0}
        >
          <FiTruck size={14} />
          {booking ? 'Booking…' : `Book ${selected.size} Order${selected.size !== 1 ? 's' : ''}`}
        </button>
      </div>

      <datalist id="cc-cities-datalist">
        {cities.map(c => <option key={c.CityId} value={c.CityName} />)}
      </datalist>

      <div className="book-table-wrap">
        <table className="book-table">
          <thead>
            <tr>
              <th style={{ width: 36 }}>
                <input type="checkbox" checked={allChecked} onChange={toggleAll} />
              </th>
              <th>Ref Number</th>
              <th>Consignee Name (*)</th>
              <th>Phone Number (*)</th>
              <th>Amount (*)</th>
              <th>kg (*)</th>
              <th>Consignee address (*)</th>
              <th>Consignee City (*)</th>
              <th>Description (*)</th>
              <th style={{ width: 72 }}>Pieces (*)</th>
              <th>Order Note</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.order_id} className={selected.has(row.order_id) ? 'book-row-selected' : ''}>
                <td>
                  <input
                    type="checkbox"
                    checked={selected.has(row.order_id)}
                    onChange={() => toggleRow(row.order_id)}
                  />
                </td>
                <td className="book-cell-id">{row.order_number}</td>
                <td>
                  <input
                    className="book-input"
                    value={row.consignee_name}
                    onChange={e => upd(row.order_id, 'consignee_name', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    className="book-input"
                    value={row.phone}
                    onChange={e => upd(row.order_id, 'phone', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    className={`book-input book-input-num${parseFloat(row.cod_amount) > 0 && parseFloat(row.cod_amount) < 100 ? ' book-input-error' : ''}`}
                    type="number"
                    min="0"
                    value={row.cod_amount}
                    onChange={e => upd(row.order_id, 'cod_amount', e.target.value)}
                    title={parseFloat(row.cod_amount) > 0 && parseFloat(row.cod_amount) < 100 ? 'CallCourier requires at least Rs 100' : ''}
                  />
                  {parseFloat(row.cod_amount) === 0 && (
                    <div className="book-input-hint">Non-COD / Prepaid</div>
                  )}
                  {parseFloat(row.cod_amount) > 0 && parseFloat(row.cod_amount) < 100 && (
                    <div className="book-input-hint book-input-hint-error">Min Rs 100</div>
                  )}
                </td>
                <td>
                  <input
                    className="book-input book-input-xs"
                    type="number"
                    min="0"
                    step="0.1"
                    value={row.weight}
                    onChange={e => upd(row.order_id, 'weight', e.target.value)}
                  />
                </td>
                <td>
                  <textarea
                    className="book-textarea"
                    rows={3}
                    value={row.address}
                    onChange={e => upd(row.order_id, 'address', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    className={`book-input${submitAttempted && selected.has(row.order_id) && !row.dest_city_id ? ' book-input-error' : ''}`}
                    list="cc-cities-datalist"
                    placeholder="Type to search city…"
                    value={row.dest_city_name}
                    onChange={e => updCity(row.order_id, e.target.value)}
                  />
                  {submitAttempted && selected.has(row.order_id) && !row.dest_city_id && (
                    <div className="book-input-hint book-input-hint-error">City is required</div>
                  )}
                </td>
                <td>
                  <textarea
                    className="book-textarea"
                    rows={3}
                    value={row.description}
                    onChange={e => upd(row.order_id, 'description', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    className="book-input book-input-xs"
                    type="number"
                    min="1"
                    value={row.pcs}
                    onChange={e => upd(row.order_id, 'pcs', e.target.value)}
                  />
                </td>
                <td>
                  <textarea
                    className="book-textarea"
                    rows={3}
                    placeholder="Order Note"
                    value={row.note}
                    onChange={e => upd(row.order_id, 'note', e.target.value)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AdminBookOrders;
